import type {
  ResearchTargetConnectionStatus,
  ResearchTargetResearchStatus,
  ResearchTargets,
} from "@/services/types/agent-gateway";
import {
  BadgeCheck,
  Building2,
  Check,
  ChevronDown,
  CircleDashed,
  CircleX,
  Linkedin,
  Mail,
  Phone,
  Search,
  Send,
} from "lucide-react";
import { useState } from "react";

type ResearchTargetsPanelProps = {
  researchTargets: ResearchTargets | null;
};

const contactIcons = {
  email: Mail,
  linkedin: Linkedin,
  phone: Phone,
  whatsapp: Send,
} as const;

const connectionStatusPresentation: Record<
  ResearchTargetConnectionStatus,
  { className: string; label: string }
> = {
  checking: {
    className: "bg-[#E9ECEF] text-[#6C757D]",
    label: "Verifying connection…",
  },
  connection_check_failed: {
    className: "bg-[rgba(220,53,69,0.12)] text-[#A61E2C]",
    label: "Connection check failed",
  },
  connection_request_needed: {
    className: "bg-[rgba(253,176,34,0.24)] text-[#633806]",
    label: "Connection request needed",
  },
  pending_connection_approval: {
    className: "bg-[rgba(124,58,237,0.12)] text-[#5B21B6]",
    label: "Pending connection approval",
  },
  approved_to_connect: {
    className: "bg-[rgba(110,211,120,0.16)] text-[#18500A]",
    label: "Approved to connect",
  },
  connection_request_sending: {
    className: "bg-[rgba(59,130,246,0.12)] text-[#1D4ED8]",
    label: "Sending connection request…",
  },
  awaiting_acceptance: {
    className: "bg-[rgba(6,182,212,0.12)] text-[#0E7490]",
    label: "Connection sent · awaiting acceptance",
  },
  connected: {
    className: "bg-[rgba(110,211,120,0.16)] text-[#18500A]",
    label: "Connected",
  },
  connection_request_rejected: {
    className: "bg-[rgba(220,53,69,0.12)] text-[#A61E2C]",
    label: "Connection request rejected",
  },
  connection_request_failed: {
    className: "bg-[rgba(220,53,69,0.12)] text-[#A61E2C]",
    label: "Connection request failed",
  },
  connection_delivery_unknown: {
    className: "bg-[rgba(253,176,34,0.24)] text-[#633806]",
    label: "Connection delivery needs review",
  },
  connection_acceptance_expired: {
    className: "bg-[#E9ECEF] text-[#6C757D]",
    label: "Connection acceptance expired",
  },
};

const researchStatusPresentation: Record<
  ResearchTargetResearchStatus,
  { className: string; label: string }
> = {
  research_pending: {
    className: "bg-[#E9ECEF] text-[#6C757D]",
    label: "Pending research",
  },
  in_progress: {
    className: "bg-[#E9ECEF] text-[#6C757D]",
    label: "Verifying…",
  },
  needs_review: {
    className: "bg-[rgba(253,176,34,0.24)] text-[#633806]",
    label: "Pending review",
  },
  verified: {
    className: "bg-[rgba(110,211,120,0.16)] text-[#18500A]",
    label: "Qualified",
  },
  rejected: {
    className: "bg-[rgba(220,53,69,0.12)] text-[#A61E2C]",
    label: "Not qualified",
  },
  discovered: {
    className: "bg-[#E9ECEF] text-[#6C757D]",
    label: "Profile discovered",
  },
  verifying_profile: {
    className: "bg-[rgba(59,130,246,0.12)] text-[#1D4ED8]",
    label: "Verifying profile…",
  },
  company_verified: {
    className: "bg-[rgba(59,130,246,0.12)] text-[#1D4ED8]",
    label: "Company verified",
  },
  company_ambiguous: {
    className: "bg-[rgba(253,176,34,0.24)] text-[#633806]",
    label: "Company association unclear",
  },
  company_mismatch: {
    className: "bg-[rgba(220,53,69,0.12)] text-[#A61E2C]",
    label: "Company mismatch",
  },
  persona_ineligible: {
    className: "bg-[rgba(220,53,69,0.12)] text-[#A61E2C]",
    label: "Not eligible",
  },
  qualified: {
    className: "bg-[rgba(110,211,120,0.16)] text-[#18500A]",
    label: "Qualified",
  },
  verification_failed: {
    className: "bg-[rgba(220,53,69,0.12)] text-[#A61E2C]",
    label: "Profile verification failed",
  },
};

function getLiveLabel(targets: ResearchTargets, leadCount: number) {
  return targets.status === "searching"
    ? "Live · searching"
    : `Live · ${leadCount} found`;
}

function getQualificationLabel(
  status: ResearchTargets["companies"][number]["qualification_status"],
) {
  if (status === "shortlisted") {
    return {
      className: "bg-[#E9ECEF] text-[#495057]",
      label: "Shortlisted",
    };
  }

  if (status === "qualified") {
    return {
      className: "bg-[rgba(110,211,120,0.16)] text-[#18500A]",
      label: "Qualified",
    };
  }

  if (status === "rejected") {
    return {
      className: "bg-[rgba(220,53,69,0.12)] text-[#A61E2C]",
      label: "Not qualified",
    };
  }

  if (status === "skipped") {
    return {
      className: "bg-[#E9ECEF] text-[#495057]",
      label: "Skipped",
    };
  }

  return {
    className: "bg-[rgba(253,176,34,0.24)] text-[#633806]",
    label: "Researching",
  };
}

function getLeadStatusLabel(
  lead: ResearchTargets["companies"][number]["leads"][number],
) {
  if (lead.connection_status) {
    return connectionStatusPresentation[lead.connection_status];
  }

  if (lead.approval_status === "approved") {
    return {
      className: "bg-[#6ED378] text-[#18500A]",
      label: "Approved",
    };
  }

  if (lead.research_status) {
    return researchStatusPresentation[lead.research_status];
  }

  return {
    className: "bg-[rgba(253,176,34,0.24)] text-[#633806]",
    label: "Pending review",
  };
}

function LeadStatusBadge({
  lead,
}: {
  lead: ResearchTargets["companies"][number]["leads"][number];
}) {
  const status = getLeadStatusLabel(lead);
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-center font-medium text-sm leading-tight ${status.className}`}
    >
      {status.label}
    </span>
  );
}

function getNoLeadsMessage(
  status: ResearchTargets["companies"][number]["qualification_status"],
) {
  if (status === "shortlisted") {
    return "Shortlisted for research.";
  }

  if (status === "skipped") {
    return "Research was skipped for this company.";
  }

  return status === "qualified"
    ? "No decision-makers are available yet."
    : "No decision-makers found yet.";
}

function getDeliveryLabel(
  delivery: NonNullable<
    ResearchTargets["companies"][number]["leads"][number]["delivery"]
  >,
) {
  const channel =
    delivery.channel === "linkedin"
      ? "LinkedIn"
      : delivery.channel === "whatsapp"
        ? "WhatsApp"
        : delivery.channel;

  if (delivery.status === "sending") {
    return `Sending via ${channel}…`;
  }

  const sentAt = delivery.sent_at
    ? new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date(delivery.sent_at))
    : null;

  return `Sent via ${channel}${sentAt ? ` · ${sentAt}` : ""}`;
}

function getContactLink(channel: keyof typeof contactIcons, value: string) {
  if (channel === "email") {
    return `mailto:${value}`;
  }
  if (channel === "phone" || channel === "whatsapp") {
    return `tel:${value.replace(/\s/g, "")}`;
  }
  return value.startsWith("http") ? value : `https://${value}`;
}

function getContactStatus(
  status: ResearchTargets["companies"][number]["leads"][number]["contacts"][number]["display_status"],
) {
  if (status === "verified") {
    return {
      Icon: BadgeCheck,
      className: "bg-[rgba(110,211,120,0.24)] text-[#18500A]",
      label: "Verified",
    };
  }

  if (status === "verifying") {
    return {
      Icon: CircleDashed,
      className: "bg-[#E9ECEF] text-[#6C757D]",
      label: "Verifying…",
    };
  }

  return {
    Icon: null,
    className: "bg-[#E9ECEF] text-[#6C757D]",
    label: "Found",
  };
}

export function ResearchTargetsPanel({
  researchTargets,
}: ResearchTargetsPanelProps) {
  const [researchTargetsExpanded, setResearchTargetsExpanded] = useState(true);
  const [expandedCompanyId, setExpandedCompanyId] = useState<
    string | null | undefined
  >(undefined);

  if (!researchTargets) {
    return null;
  }

  const hasSelectedCompany =
    expandedCompanyId !== null &&
    researchTargets.companies.some(
      (company) => company.id === expandedCompanyId,
    );
  const openCompanyId =
    expandedCompanyId === undefined ||
    (expandedCompanyId !== null && !hasSelectedCompany)
      ? (researchTargets.companies[0]?.id ?? null)
      : expandedCompanyId;

  const leadCount = researchTargets.companies.reduce(
    (count, company) => count + company.leads.length,
    0,
  );

  return (
    <section
      aria-labelledby="research-targets-heading"
      className="rounded-xl border border-[#E9ECEF] bg-white px-5 py-[18px]"
    >
      <div className="flex items-center justify-between gap-3">
        <h2
          aria-label="Research Targets"
          className="font-medium text-[#212529] text-base"
          id="research-targets-heading"
        >
          <span className="hidden sm:inline">Research Targets</span>
          <button
            aria-controls="research-targets-content"
            aria-expanded={researchTargetsExpanded}
            className="flex w-full items-center justify-between gap-3 text-left focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 sm:hidden"
            onClick={() => setResearchTargetsExpanded((expanded) => !expanded)}
            type="button"
          >
            Research Targets
            <ChevronDown
              aria-hidden="true"
              className={`size-4 shrink-0 text-[#6C757D] transition-transform motion-reduce:transition-none ${
                researchTargetsExpanded ? "rotate-180" : ""
              }`}
            />
          </button>
        </h2>
        <span
          aria-live="polite"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[rgba(36,12,46,0.16)] px-2.5 py-1 font-medium text-[#240C2E] text-sm"
        >
          <span className="size-[7px] rounded-full bg-[#240C2E]" />
          {getLiveLabel(researchTargets, leadCount)}
        </span>
      </div>

      <div
        className={
          researchTargetsExpanded ? "block sm:block" : "hidden sm:block"
        }
        id="research-targets-content"
      >
        <div className="mt-3">
          {researchTargets.status === "searching" ? (
            <div className="flex items-center gap-2 rounded-lg border border-[#CED5DA] border-dashed px-3 py-2.5 text-[#6C757D] text-sm">
              <Search aria-hidden="true" className="size-3.5" />
              {researchTargets.activity_label ??
                "Searching for research targets…"}
            </div>
          ) : null}

          {researchTargets.companies.length > 0 ? (
            <div
              className={
                researchTargets.status === "searching"
                  ? "mt-3 space-y-3"
                  : "space-y-3"
              }
            >
              {researchTargets.companies.map((company) => {
                const qualification = getQualificationLabel(
                  company.qualification_status,
                );

                return (
                  <article
                    className="rounded-lg border border-[#CED5DA]"
                    key={company.id}
                  >
                    <button
                      aria-controls={`research-company-${company.id}`}
                      aria-expanded={openCompanyId === company.id}
                      className="flex w-full items-start gap-2 px-3 py-2.5 text-left hover:bg-[#F8F9FA] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                      onClick={() =>
                        setExpandedCompanyId(
                          openCompanyId === company.id ? null : company.id,
                        )
                      }
                      type="button"
                    >
                      <Building2
                        aria-hidden="true"
                        className="mt-0.5 size-3.5 shrink-0 text-[#6C757D]"
                      />
                      <span className="min-w-0 flex-1 break-words font-medium text-[#212529] text-sm">
                        {company.name}
                      </span>
                      <span className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-2">
                        <span
                          className={`rounded-full px-2 py-0.5 font-medium text-sm ${qualification.className}`}
                        >
                          {qualification.label}
                        </span>
                        <span className="text-[#6C757D] text-sm">
                          {company.leads.length}{" "}
                          {company.leads.length === 1 ? "lead" : "leads"}
                        </span>
                      </span>
                      <ChevronDown
                        aria-hidden="true"
                        className={`mt-0.5 size-4 shrink-0 text-[#6C757D] transition-transform motion-reduce:transition-none ${
                          openCompanyId === company.id ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {openCompanyId === company.id ? (
                      <div id={`research-company-${company.id}`}>
                        {company.qualification_status === "rejected" ? (
                          <div className="border-[#E9ECEF] border-t px-3 py-2.5 pl-[34px]">
                            <p className="flex items-center gap-2 font-medium text-[#A61E2C] text-sm">
                              <CircleX
                                aria-hidden="true"
                                className="size-3.5"
                              />
                              Not qualified
                            </p>
                            {company.rejection_reasons.length > 0 ? (
                              <ul className="mt-1.5 list-disc space-y-1 pl-5 text-[#6C757D] text-sm">
                                {company.rejection_reasons.map((reason) => (
                                  <li key={reason}>{reason}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="mt-1.5 text-[#6C757D] text-sm">
                                This company did not meet the campaign criteria.
                              </p>
                            )}
                          </div>
                        ) : null}

                        {company.leads.length === 0 &&
                        company.qualification_status !== "rejected" ? (
                          <p className="border-[#E9ECEF] border-t px-3 py-2.5 pl-[34px] text-[#6C757D] text-sm">
                            {getNoLeadsMessage(company.qualification_status)}
                          </p>
                        ) : null}

                        {company.leads.map((lead) => (
                          <div
                            className="border-[#E9ECEF] border-t px-3 py-2.5 pl-[34px]"
                            key={lead.id}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <p className="min-w-0 truncate font-medium text-[#212529] text-sm">
                                {lead.name}
                              </p>
                              <LeadStatusBadge lead={lead} />
                            </div>

                            {lead.role_title ? (
                              <p className="mt-1 text-[#6C757D] text-sm">
                                {lead.role_title}
                              </p>
                            ) : null}

                            {lead.contacts.map((contact) => {
                              const Icon = contactIcons[contact.channel];
                              const status = getContactStatus(
                                contact.display_status,
                              );
                              const StatusIcon = status.Icon;
                              return (
                                <a
                                  className="mt-1.5 flex items-center gap-2 break-all text-[#343A40] text-sm hover:underline"
                                  href={getContactLink(
                                    contact.channel,
                                    contact.value,
                                  )}
                                  key={`${contact.channel}:${contact.value}`}
                                  rel={
                                    contact.channel === "linkedin"
                                      ? "noreferrer"
                                      : undefined
                                  }
                                  target={
                                    contact.channel === "linkedin"
                                      ? "_blank"
                                      : undefined
                                  }
                                >
                                  <Icon
                                    aria-hidden="true"
                                    className="size-3.5 shrink-0"
                                  />
                                  <span>{contact.value}</span>
                                  <span
                                    className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 font-medium text-xs ${status.className}`}
                                  >
                                    {StatusIcon ? (
                                      <StatusIcon
                                        aria-hidden="true"
                                        className="size-3"
                                      />
                                    ) : null}
                                    {status.label}
                                  </span>
                                </a>
                              );
                            })}

                            {lead.delivery ? (
                              <p
                                className={
                                  lead.delivery.status === "sending"
                                    ? "mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#FDB022] border-dashed bg-[rgba(253,176,34,0.16)] px-2 py-1 font-medium text-[#633806] text-sm"
                                    : "mt-2 inline-flex items-center gap-1.5 rounded-full bg-[rgba(110,211,120,0.16)] px-2 py-1 font-medium text-[#18500A] text-sm"
                                }
                              >
                                {lead.delivery.status === "sent" ? (
                                  <Check
                                    aria-hidden="true"
                                    className="size-3.5"
                                  />
                                ) : (
                                  <Send
                                    aria-hidden="true"
                                    className="size-3.5"
                                  />
                                )}
                                {getDeliveryLabel(lead.delivery)}
                              </p>
                            ) : null}

                            {lead.awaiting_reply ? (
                              <p className="mt-1.5 text-[#6C757D] text-sm">
                                Awaiting reply
                              </p>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          ) : researchTargets.status === "searching" ? (
            <div aria-hidden="true" className="mt-3 space-y-3">
              <div className="h-3 w-[78%] animate-pulse rounded bg-[#E9ECEF] motion-reduce:animate-none" />
              <div className="h-3 w-[58%] animate-pulse rounded bg-[#E9ECEF] motion-reduce:animate-none" />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
